import React, { useEffect, useState } from 'react';
import { Link, useLocation, useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { auth, db } from './firebaseConfig';
import { signOut } from 'firebase/auth';
import { collection, query, where, getDocs } from 'firebase/firestore';
import './LinkedInHeaderStyles.css';

function MobileBottomNav({ userProfile, showBusinessMenu, setShowBusinessMenu, showMeMenu, setShowMeMenu }) {
  const { t, i18n } = useTranslation();
  const location = useLocation();
  const navigate = useNavigate();
  const isActive = (path) => location.pathname === path ? 'active' : '';
  
  const roles = userProfile?.roles || [];
  const hasBusinessAccess = roles.some(role => ['admin', 'sales', 'hr', 'hm'].includes(role));
  
  const defaultAvatar = "data:image/svg+xml;base64,PHN2ZyB4bWxucz0iaHR0cDovL3d3dy53My5vcmcvMjAwMC9zdmciIHZpZXdCb3g9IjAgMCAyNCAyNCIgZmlsbD0iI2NjYyI+PHBhdGggZD0iTTEyIDEyYzIuMjEgMC00LTEuNzktNC00czEuNzktNCA0LTRzNCAxLjc5IDQgNHMtMS43OSA0LTQgNHptMCAyYy0yLjY3IDAtOCAxLjM0LTggNHYyaDE2di0yYy04LTIuNjYtNS4zMy00LTgtNHoiLz48L3N2Zz4=";
  const avatarUrl = userProfile?.avatar_url || defaultAvatar;

  const [latestVtcId, setLatestVtcId] = useState(null);

  // Close menus on route change
  useEffect(() => {
    setShowBusinessMenu(false);
    setShowMeMenu(false);
  }, [location.pathname]);

  useEffect(() => {
    const fetchLatestVtc = async () => {
      const user = auth.currentUser;
      if (user && showMeMenu) {
        try {
          const q = query(
            collection(db, 'talentCards'),
            where('userId', '==', user.uid)
          );
          const querySnapshot = await getDocs(q);
          if (!querySnapshot.empty) {
             const docs = querySnapshot.docs.map(doc => ({ id: doc.id, ...doc.data() }));
             // Sort in memory to avoid index requirements
             docs.sort((a, b) => {
                const dateA = a.createdAt?.toDate ? a.createdAt.toDate() : new Date(0);
                const dateB = b.createdAt?.toDate ? b.createdAt.toDate() : new Date(0);
                return dateB - dateA; 
            });
            setLatestVtcId(docs[0].id);
          }
        } catch (error) {
          console.error("Error fetching VTC for mobile menu:", error);
        }
      }
    };
    fetchLatestVtc();
  }, [showMeMenu]);

  const toggleBusinessMenu = () => {
      setShowBusinessMenu(!showBusinessMenu);
      setShowMeMenu(false);
  };

  const toggleMeMenu = () => {
      setShowMeMenu(!showMeMenu);
      setShowBusinessMenu(false);
  };

  const closeMenus = () => {
      setShowBusinessMenu(false);
      setShowMeMenu(false);
  };

  const handleNavigation = (path, closeFn) => {
      setTimeout(() => {
          closeFn(false);
          navigate(path);
      }, 300);
  };

  const handleShareDigitalTwin = async () => {
      if (!latestVtcId) return;
      
      const url = `${window.location.origin}/digital-twin/${latestVtcId}`;
      const shareData = {
          title: 'My AI Twin',
          text: 'Check out my AI AI Twin on POD+!',
          url: url
      };

      if (navigator.share) {
          try {
              await navigator.share(shareData);
              setShowMeMenu(false);
          } catch (err) {
              console.error('Error sharing:', err);
          }
      } else {
          navigator.clipboard.writeText(url);
          alert(t('linkCopied', 'Link copied to clipboard!'));
          setShowMeMenu(false);
      }
  };

  const handleLogout = async () => {
    // Delay to show click effect
    setTimeout(async () => {
        setShowMeMenu(false);
        try {
          await signOut(auth);
        } catch (error) {
          console.error('Error signing out:', error);
        }
    }, 300);
  };

  const overlayStyle = {
      position: 'fixed',
      bottom: '60px',
      left: 0,
      width: '100%',
      backgroundColor: 'white',
      borderTop: '1px solid #e0e0e0',
      boxShadow: '0 -4px 16px rgba(0,0,0,0.15)',
      zIndex: 1001,
      display: 'flex',
      flexDirection: 'column',
      maxHeight: '70vh',
      overflowY: 'auto'
  };

  const headerStyle = {
      padding: '16px',
      fontSize: '16px', 
      fontWeight: '600', 
      color: 'rgba(0,0,0,0.9)',
      margin: 0,
      borderBottom: '1px solid #e0e0e0',
      backgroundColor: '#f9f9f9'
  };

  const BusinessMenuOverlay = () => (
      <div style={overlayStyle}>
          <h3 style={headerStyle}>{t('mobileNav.forBusiness')}</h3>
          {roles.includes('admin') && <button className="mobile-menu-item" onClick={() => handleNavigation("/admin", setShowBusinessMenu)}>{t('adminDashboard.title')}</button>}
          {roles.includes('sales') && <button className="mobile-menu-item" onClick={() => handleNavigation("/sales-dashboard", setShowBusinessMenu)}>{t('salesDashboard.title')}</button>}
          {roles.includes('hr') && <button className="mobile-menu-item" onClick={() => handleNavigation("/hr-dashboard", setShowBusinessMenu)}>{t('hrDashboard.title')}</button>}
          {roles.includes('hr') && <button className="mobile-menu-item" onClick={() => handleNavigation("/talent-pool", setShowBusinessMenu)}>{t('sharedTalentPool.talentPool')}</button>}
          {roles.includes('hm') && <button className="mobile-menu-item" onClick={() => handleNavigation("/start-hiring", setShowBusinessMenu)}>{t('mobileNav.hiring')}</button>}
      </div>
  );

  const MeMenuOverlay = () => (
      <div style={overlayStyle}>
          <h3 style={headerStyle}>{t('mobileNav.me')}</h3>
          <button className="mobile-menu-item" onClick={() => handleNavigation("/profile", setShowMeMenu)}>{t('myProfile.title')}</button>
          {latestVtcId && (
            <button className="mobile-menu-item" onClick={handleShareDigitalTwin}>
                {t('myProfile.shareMenuTitle')}
            </button>
          )}
          <button className="mobile-menu-item" onClick={() => handleNavigation(latestVtcId ? `/digital-twin/${latestVtcId}` : '/chat', setShowMeMenu)}>
            {t('talkToTwin')}
          </button>
          
          <button className="mobile-menu-item" onClick={() => handleNavigation("/agent-performance", setShowMeMenu)}>{t('simulations.quickTip5Title')}</button>
          <button className="mobile-menu-item" style={{color: '#cc1016'}} onClick={handleLogout}>{t('logOut')}</button>
      </div>
  );

  return (
    <>
      {(showBusinessMenu || showMeMenu) && <div onClick={() => { setShowBusinessMenu(false); setShowMeMenu(false); }} style={{position: 'fixed', top:0, left:0, width:'100%', height:'100%', zIndex: 1000}}></div>}
      {showBusinessMenu && <BusinessMenuOverlay />}
      {showMeMenu && <MeMenuOverlay />}
      
      <nav className="mobile-bottom-nav">
        <Link to="/welcome" className={`mobile-nav-item ${isActive('/welcome')}`} onClick={closeMenus}>
          <svg className="mobile-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M23 9v2h-2v7a3 3 0 0 1-3 3h-4v-6h-4v6H6a3 3 0 0 1-3-3v-7H1V9l11-7z"></path>
          </svg>
          <span>{t('mobileNav.home')}</span>
        </Link>
        
        <Link to="/simulations" className={`mobile-nav-item ${isActive('/simulations')}`} onClick={closeMenus}>
           <svg className="mobile-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" strokeLinejoin="round" style={{ width: '48px', height: '48px', marginBottom: '0px' }}>
            <rect x="2" y="9" width="8" height="6" rx="1" />
            <path d="M6 15v2" />
            <path d="M6 9V7" />
            <path d="M4 11h1" />
            <path d="M8 11h-1" />
            <rect x="14" y="9" width="8" height="6" rx="1" />
            <path d="M18 15v2" />
            <path d="M18 9V7" />
            <path d="M16 11h1" />
            <path d="M20 11h-1" />
            <path d="M11 10h2" />
            <path d="M11 14h2" />
           </svg>
           <span>{t('mobileNav.sims')}</span>
        </Link>
        
        <Link to="/chat?reset=true" className={`mobile-nav-item ${isActive('/chat')}`} onClick={closeMenus}>
          <svg className="mobile-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
            <path d="M16 4H8a7 7 0 0 0 0 14h4v4l8.16-5.39A6.78 6.78 0 0 0 23 11a7 7 0 0 0-7-7m-8 8.25A1.25 1.25 0 1 1 9.25 11 1.25 1.25 0 0 1 8 12.25m4 0A1.25 1.25 0 1 1 13.25 11 1.25 1.25 0 0 1 12 12.25m4 0A1.25 1.25 0 1 1 17.25 11 1.25 1.25 0 0 1 16 12.25"></path>
          </svg>
          <span>{t('mobileNav.chat')}</span>
        </Link>

        {hasBusinessAccess && (
            <div className={`mobile-nav-item ${showBusinessMenu ? 'active' : ''}`} onClick={toggleBusinessMenu}>
               <svg className="mobile-nav-icon" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24">
                <path d="M2 2h2.67v2.67H2zm4.67 2.67h2.67V2H6.67zM11.34 2v2.67h2.67V2zM2 9.33h2.67V6.66H2zm4.67 0h2.67V6.66H6.67zm4.67 0h2.67v-2.67H6.67zm4.67 0h2.67v-2.67h-2.67zM2 14h2.67v-2.67H2zm4.67 0h2.67v-2.67H6.67zm4.67 0h2.67v-2.67h-2.67z"></path>
               </svg>
               <span>{t('mobileNav.business')}</span>
            </div>
        )}

        <div className={`mobile-nav-item ${showMeMenu ? 'active' : ''}`} onClick={toggleMeMenu}>
          <img src={avatarUrl} alt={t('mobileNav.me')} className="mobile-nav-avatar" />
          <span>{t('mobileNav.me')}</span>
        </div>
      </nav>
    </>
  );
}

export default MobileBottomNav;